import { useState, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone, MessageCircle } from 'lucide-react';
import { CONTACT_INFO } from '@/lib/data';
import { useSettings } from '@/lib/useSettings';
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetClose
} from '@/components/ui/sheet';

const NAV_LINKS = [
  { href: '/', label: 'Anasayfa' },
  { href: '/hizmetler', label: 'Tedaviler' },
  { href: '/surec', label: 'Tedavi Süreci' },
  { href: '/hakkimizda', label: 'Hakkımda' },
  { href: '/haberler', label: 'Haberler' },
  { href: '/icerikler', label: 'İçerikler' },
  { href: '/iletisim', label: 'İletişim' },
  { href: '/danismanlik', label: 'Sade Düşün' },
];

export const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const settings = useSettings();
  const CONTACT_INFO = settings.contact || {};

  const isActive = useCallback((href) => {
    if (href === '/') return location.pathname === '/';
    return location.pathname === href || location.pathname.startsWith(href + '/');
  }, [location.pathname]);

  return (
    <header
      className="sticky top-0 z-50 border-b border-[#E7E5E4] bg-white/88 backdrop-blur-[20px] supports-[backdrop-filter]:bg-white/88"
      data-testid="header-nav"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-10">
        <div className="flex h-16 items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo">
            <img src={settings.doctor?.image || 'https://customer-assets.emergentagent.com/job_dr-nilufershop/artifacts/rd2kgze0_image.png'} alt="Prof. Dr. Nilüfer Yıldırım" className="w-11 h-11 sm:w-9 sm:h-9 rounded-full object-cover" />
            <div className="hidden sm:block">
              <p className="font-heading font-semibold text-sm text-[#1F2937] leading-tight">Prof. Dr. Nilüfer Yıldırım</p>
              <p className="text-[11px] text-[#5F6B76] leading-tight">Bütüncül Sağlık &amp; Fonksiyonel Tıp</p>
            </div>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                data-testid={`header-${link.href.slice(1)}-link`}
                className={`px-3 py-2 rounded-full text-sm font-medium transition-colors ${
                  isActive(link.href)
                    ? 'text-[#1F6E7A] bg-[#1F6E7A]/8'
                    : 'text-[#5F6B76] hover:text-[#1F2937] hover:bg-[#F5F3EF]'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Right Actions */}
          <div className="flex items-center gap-2">
            <Link
              to="/iletisim"
              className="hidden sm:inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold bg-[#1F6E7A] text-white hover:bg-[#185960] transition-colors shadow-[0_8px_24px_-12px_rgba(31,110,122,0.4)]"
              data-testid="header-appointment-button"
            >
              Randevu Al
            </Link>

            {/* Mobile Menu */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  className="lg:hidden flex items-center justify-center w-12 h-12 rounded-full hover:bg-[#F5F3EF] transition-colors"
                  data-testid="header-mobile-menu-button"
                  aria-label="Menü"
                >
                  <Menu size={32} />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="w-[300px] bg-white p-0">
                <div className="flex flex-col h-full">
                  <div className="p-5 border-b border-[#E7E5E4]">
                    <p className="font-heading font-semibold text-xl text-[#1F2937]">Prof. Dr. Nilüfer Yıldırım</p>
                    <p className="text-base text-[#5F6B76]">Bütüncül Sağlık &amp; Fonksiyonel Tıp</p>
                  </div>
                  <nav className="flex-1 p-4 space-y-1">
                    {NAV_LINKS.map((link) => (
                      <SheetClose asChild key={link.href}>
                        <Link
                          to={link.href}
                          className={`flex items-center px-4 py-3 rounded-2xl text-lg font-medium transition-colors ${
                            isActive(link.href)
                              ? 'text-[#1F6E7A] bg-[#1F6E7A]/8'
                              : 'text-[#1F2937] hover:bg-[#F5F3EF]'
                          }`}
                        >
                          {link.label}
                        </Link>
                      </SheetClose>
                    ))}
                  </nav>
                  <div className="p-4 space-y-2 border-t border-[#E7E5E4]">
                    <a
                      href={`tel:${CONTACT_INFO.phone}`}
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-full border border-[#E7E5E4] text-lg font-medium text-[#1F2937] hover:bg-[#F5F3EF] transition-colors"
                    >
                      <Phone size={16} /> Ara
                    </a>
                    <a
                      href={`https://wa.me/${CONTACT_INFO.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full py-3 rounded-full border border-[#1F6E7A] text-lg font-medium text-[#1F6E7A] hover:bg-[#1F6E7A]/5 transition-colors"
                    >
                      <MessageCircle size={16} /> WhatsApp
                    </a>
                    <SheetClose asChild>
                      <Link
                        to="/iletisim"
                        className="flex items-center justify-center gap-2 w-full py-3 rounded-full bg-[#1F6E7A] text-lg font-semibold text-white hover:bg-[#185960] transition-colors"
                      >
                        Randevu Al
                      </Link>
                    </SheetClose>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};
